import React from 'react';
import { motion } from 'framer-motion';
import { Quote, TrendingUp } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';

const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'Rajesh Kumar',
      company: 'TechVision Solutions',
      testimonial: 'Stellars Estates transformed our digital presence completely. Their ad campaigns increased our leads by 320% in just 3 months. The ROI has been exceptional!',
      results: '320% increase in leads',
      metric: '4.8x ROI'
    },
    {
      name: 'Priya Mehta',
      company: 'Fashion Hub India',
      testimonial: 'The social media marketing strategy they created for us was game-changing. Our engagement skyrocketed and we saw a 250% boost in online sales within 2 months.',
      results: '250% sales increase',
      metric: '5M+ impressions'
    },
    {
      name: 'Amit Patel',
      company: 'GreenLeaf Organics',
      testimonial: 'Our new website developed by Stellars Estates not only looks stunning but converts visitors into customers. Conversion rate improved by 180% and bounce rate decreased significantly.',
      results: '180% conversion boost',
      metric: '40% less bounce rate'
    },
    {
      name: 'Sneha Desai',
      company: 'Elite Fitness Studio',
      testimonial: 'The WhatsApp marketing campaigns helped us reach customers directly. We achieved a 95% open rate and generated 400+ qualified leads in the first month alone.',
      results: '400+ qualified leads',
      metric: '95% open rate'
    }
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 35, scale: 0.97 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, ease: [0.215, 0.61, 0.355, 1] }
    }
  };

  return (
    <section 
      className="py-16 sm:py-20 lg:py-24 bg-[#181F32] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
          className="text-center mb-12 sm:mb-16"
        >
          <div className="inline-block mb-3">
            <span className="glass-badge">
              Proven Client Growth
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 text-white font-heading">
            Client <span className="text-[#F3453F]">Success Stories</span>
          </h2>
          <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto font-sans font-medium">
            Real performance results and revenue growth generated for businesses like yours
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
        >
          {testimonials.map((item, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -6, scale: 1.01, transition: { duration: 0.25, ease: 'easeOut' } }}
              className="glass-panel-white p-6 sm:p-8 flex flex-col justify-between group transition-all duration-300"
            >
              <div>
                <div className="flex items-start gap-4 mb-4">
                  <div className="glass-icon-pod w-12 h-12 p-3 flex-shrink-0 text-[#F3453F] group-hover:border-[#F3453F] transition-all duration-300">
                    <Quote size={24} />
                  </div>
                  <p className="text-[#181F32] leading-relaxed italic font-sans text-sm sm:text-base font-semibold">
                    "{item.testimonial}"
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200/80">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3.5">
                    <div className="glass-avatar-frame p-0.5">
                      <div className="w-11 h-11 rounded-full bg-[#181F32] text-white flex items-center justify-center font-extrabold text-base font-heading border border-white/40 relative overflow-hidden">
                        <span className="relative z-10">{item.name.charAt(0)}</span>
                      </div>
                    </div>
                    <div>
                      <p className="font-extrabold text-base sm:text-lg text-[#181F32] font-heading group-hover:text-[#F3453F] transition-colors">
                        {item.name}
                      </p>
                      <p className="text-xs sm:text-sm text-[#181F32]/70 font-sans font-bold">{item.company}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <div className="glass-result-tag">
                    <TrendingUp size={16} className="text-[#F3453F] flex-shrink-0" />
                    <span><AnimatedCounter value={item.results} /></span>
                  </div>
                  <div className="glass-metric-pill">
                    <AnimatedCounter value={item.metric} />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
